import { computed, type ComputedRef } from 'vue'
import { UNLIMITED_CAPACITY } from '~/components/PurchaseContract/Program/course-capacity'
import type {
    BookingPeriodAvailability,
    BookingStatusAvailableResult,
    CourseBookingStatus
} from '~/resources/graphql/graphql'
import { useViewer } from '#imports'

export function useCourseAvailabilityMessage(
    waitlistEnabled: ComputedRef<boolean>,
    availabilityThreshold: ComputedRef<number | null>,
    courseData: ComputedRef<{
        bookingStatus: CourseBookingStatus
        bookingStatusAvailable: BookingStatusAvailableResult[]
        bookingPeriodAvailable: BookingPeriodAvailability
        capacityResult: {
            isWaitlistFull: boolean
            overlappedBookedSessions: number
            contracts: number
            enrollments: number
        }
    }>
) {
    const viewer = useViewer()

    return computed<{
        message: string
        theme: 'danger' | 'success' | 'base'
    }>(() => {
        // Booking status closed
        if (courseData.value.bookingStatus === 'CLOSED') {
            // No joining waitlist when restricted in this way
            return {
                message: 'Booking is closed',
                theme: 'danger'
            }
        }

        // If the asset schedule isn't allowing more sessions to be booked, you can't book
        if (courseData.value.capacityResult.overlappedBookedSessions <= 0) {
            // No joining waitlist when restricted in this way
            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // If no more contracts are allowed to be booked
        if (courseData.value.capacityResult.contracts <= 0) {
            if (waitlistEnabled.value) {
                if (courseData.value.capacityResult.isWaitlistFull) {
                    return {
                        message: 'Course and waitlist full',
                        theme: 'danger'
                    }
                } else {
                    return {
                        message: 'Course is full. Join the waitlist below',
                        theme: 'danger'
                    }
                }
            }

            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // If available enrollments is 0, return full
        // There is an exception if the customer is on the waitlist
        const enrollmentCapacity = courseData.value.capacityResult.enrollments
        if (enrollmentCapacity <= 0) {
            if (waitlistEnabled.value) {
                if (
                    courseData.value.bookingStatus === 'WAITLIST' &&
                    courseData.value.bookingStatusAvailable.some(
                        status => status.overrideCapacityConstraints
                    )
                ) {
                    return {
                        message: 'Your spot is available',
                        theme: 'success'
                    }
                } else if (courseData.value.capacityResult.isWaitlistFull) {
                    return {
                        message: 'Course and waitlist full',
                        theme: 'danger'
                    }
                } else {
                    return {
                        message: 'Course is full. Join the waitlist below',
                        theme: 'danger'
                    }
                }
            }

            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // If our booking period is not available, return booking period message
        if (
            courseData.value.bookingPeriodAvailable === 'BEFORE_BOOKING_PERIOD'
        ) {
            return {
                message: 'NOT AVAILABLE YET',
                theme: 'base'
            }
        } else if (
            courseData.value.bookingPeriodAvailable === 'AFTER_BOOKING_PERIOD'
        ) {
            return {
                message: 'Booking period closed',
                theme: 'danger'
            }
        }

        // If booking status is waitlist, restricted booking
        if (courseData.value.bookingStatus === 'WAITLIST') {
            if (viewer.value) {
                // Waitlist spot is ready
                if (courseData.value.bookingStatusAvailable.length > 0) {
                    return {
                        message: 'Your spot is available',
                        theme: 'success'
                    }
                } else {
                    let message = 'Waitlist booking only'

                    if (waitlistEnabled.value) {
                        if (courseData.value.capacityResult.isWaitlistFull) {
                            message += '. Waitlist is full'
                        } else {
                            message += '. Join the waitlist below'
                        }
                    }

                    return {
                        message,
                        theme: 'danger'
                    }
                }
            } else {
                return {
                    message: 'Waitlist only. Sign in required',
                    theme: 'danger'
                }
            }
        }

        // --Course Available--
        // If we have more capacity than the availability display threshold, or there is unlimited capacity
        if (
            (availabilityThreshold.value !== null &&
                enrollmentCapacity > availabilityThreshold.value) ||
            enrollmentCapacity === UNLIMITED_CAPACITY
        ) {
            return {
                message: 'Available',
                theme: 'base'
            }
        }

        // Otherwise list spaces available
        if (enrollmentCapacity > 1) {
            return {
                message: enrollmentCapacity + ' spaces available',
                theme: 'base'
            }
        } else if (enrollmentCapacity === 1) {
            return {
                message: '1 space available',
                theme: 'base'
            }
        }

        throw new Error()
    })
}
